// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : utility functions
// =============================================================================
// ---Third-party imports
import hash from 'hash-it';
import loadable from '@loadable/component';
import { dict, mapItems, get, isDigit, isString } from 'ldash';
// ---Globals
export const hashObj = hash;
export { MIME } from './mimetypes';
export { timeouts } from './timeouts';
import log from 'uix/logging';
export { setCustomVital } from './vitals';
export { formatSI, prettyBytes, formatDate, formatTime, formatDateTime, formatDistance, formatNumber, formatSeconds, formatMinutes, formatRegion, createDurationTime, prettyPrintXML } from './format';
import { isSSR } from './dom';
export { isSSR };
export const ucFirst = (str)=>str.charAt(0).toUpperCase() + str.slice(1);
export const lcFirst = (str)=>str.charAt(0).toLowerCase() + str.slice(1);
// we replace loadLazy with raw imports in SSR via (our) babel-plugin-transform-lazy-load
export const loadLazy = (importFuncOrPromise)=>{
    let f = importFuncOrPromise instanceof Promise ? ()=>importFuncOrPromise : importFuncOrPromise;
    return loadable(f, {
        ssr: false
    });
};
export const hashString = (str)=>{
    let h = 0;
    if (!str || str.length === 0) return h;
    for(let i = 0; i < str.length; i += 1){
        h = (h << 5) - h + str.charCodeAt(i);
        h |= 0; // Convert to 32bit integer
    }
    return h;
};
export const windowDevicePixelRatio = isSSR ? 1 : window.devicePixelRatio;
function parseRule(css) {
    let tokenizer = /\s*([a-z\-]+)\s*:\s*((?:[^;]*url\(.*?\)[^;]*|[^;]*)*)\s*(?:;|$)/gi, obj = {}, token;
    while(token = tokenizer.exec(css)){
        obj[token[1].toLowerCase()] = token[2];
    }
    return obj;
}
export const cssToReactStyle = (css)=>{
    /* transforms "flex:1;overflow:auto ; font-weight:0px;;;" to
    { flex: '1', overflow: 'auto ', fontWeight: '0px' }
  */ let style = {};
    const rules = parseRule(css);
    for (let [name, value] of Object.entries(rules)){
        if (name.includes('-')) {
            name = name.split('-').map((e, i)=>i == 0 ? e : ucFirst(e)).join('');
        }
        style[name] = value;
    }
    return style;
};
/**
 * Uses canvas.measureText to compute and return the width of the given text of given font in pixels.
 *
 * @param {String} text The text to be rendered.
 * @param {String} font The css font descriptor that text is to be rendered with (e.g. "bold 14px verdana").
 *
 * @see https://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
 * usage: getTextWidth("hello there!", "bold 12pt arial")
 */ let FONT = '12pt arial';
export const getTextWidth = (text, font)=>{
    // XXX: mock in ssr
    // re-use canvas object for better performance
    if (isSSR) {
        return text.length * 10;
    }
    let canvas = getTextWidth.canvas || (getTextWidth.canvas = document.createElement('canvas'));
    let context = canvas.getContext('2d');
    context.font = font || FONT;
    let metrics = context.measureText(text);
    return metrics.width;
};
/**
 * Calculates some approximate dimensions a given text will have after rendering.
 *
 * Currently, the dimensions are calculated by using an invisible <canvas>
 * Element. However, because of several effects (e.g. hinting), actual
 * dimensions when rendered as HTML elements can differ.
 *
 * @param {String} text
 * @param {String} font_face Font face, e.g. "Verdana".
 * @param {String} font size Height of the text in px (NO other units supported.
 *
 *
 * Returns an object with the properties width, height as double with unit px.
 * The width should be interpreted as a bounding box width of the actual text.
 *
 * The height should be interpreted as the bounding box height of a text
 * containing all possible characters rendered in the given font.
 */ // TODO: implement this with DOM element and getBoundingClientRect?
// also see:
//    https://stackoverflow.com/questions/1134586
//    https://graphicdesign.stackexchange.com/questions/4035
export const getTextDimensions = (text, font_face, font_size)=>{
    let width_guess = text.length * font_size / 2.1;
    let height_guess = font_size * 1.1;
    let width_measure = null;
    let height_measure = null;
    if (!isSSR) {
        let canvas = getTextDimensions.canvas || (getTextDimensions.canvas = document.createElement('canvas'));
        let ctx = canvas.getContext('2d');
        ctx.font = `${font_size}px "${font_face}"`;
        let metrics = ctx.measureText(text);
        width_measure = Math.abs(metrics.actualBoundingBoxLeft) + Math.abs(metrics.actualBoundingBoxRight);
        // backward compatibility for old browsers (e.g. firefox < 72 - still in ESR):
        width_measure = width_measure || 1.0 * metrics.width;
    //this does not really help to fit text in a box of given height:
    //height_measure = Math.abs(metrics.actualBoundingBoxAscent) + Math.abs(metrics.actualBoundingBoxDescent)
    }
    return {
        width: width_measure || width_guess,
        height: height_measure || height_guess
    };
};
export { darkColors, lightColors } from './colors';
// transform simple types from strings
// null => null
// true/false => bool
// ints / floats
// strings
export const decodeType = (value, emptyStringValue)=>{
    // decode only string values
    if (!isString(value)) return value;
    if (!value || value === 'null') {
        if (value == '' && emptyStringValue) {
            return emptyStringValue;
        }
        return null;
    } else if (value === 'true' || value === 'false') {
        return value === 'true';
    // match?: also seen {width: '100%'} here
    } else if (value.match?.(/^[0-9]+$/) != null) {
        let parsed = parseInt(value, 10);
        if (parsed > Number.MAX_SAFE_INTEGER) {
            // keep string form for large ints
            // see: https://stackoverflow.com/a/54092558
            return value;
        }
        return parsed;
    } else if (!isNaN(value - parseFloat(value))) {
        return parseFloat(value);
    } else return value;
};
export const decodeTypes = (data, filterFunc)=>dict(mapItems(data, (name, value)=>[
            name,
            decodeType(value)
        ], filterFunc));
// html5 empty attributes are coerced to bool true
export const decodeAttribH5 = (data, filterFunc)=>dict(mapItems(data, (name, value)=>[
            name,
            decodeType(value, true)
        ], filterFunc));
export const findElements = (element, test)=>{
    /* recursivly search for children in a JsonElement (etree like) structure
    functional xpath
  */ let result = [];
    if (element) {
        if (test(element)) {
            result.push(element);
        }
        for (let child of get(element, '$children', [])){
            result.push(...findElements(child, test));
        }
    }
    return result;
};
// return first or null
export const findElement = (element, test)=>{
    let res = findElements(element, test);
    if (res.length == 0) {
        return null;
    } else {
        return res[0];
    }
};
export const encodeSlimLink = (parentid, otid)=>`sl${parentid}_${otid}`;
export const decodeSlimLink = (sl)=>{
    if (isString(sl)) {
        let [parentid, otid] = sl.slice(2).split('_');
        if (isDigit(parentid) && isDigit(otid)) {
            return {
                parentid,
                otid
            };
        }
    }
    return {
        parentid: null,
        otid: null
    };
};
// see https://stackoverflow.com/a/30834687
export function arrayMin(arr) {
    let len = arr.length, min = Infinity;
    while(len--){
        if (Number(arr[len]) < min) {
            min = Number(arr[len]);
        }
    }
    return min;
}
export function arrayMax(arr) {
    let len = arr.length, max = -Infinity;
    while(len--){
        if (Number(arr[len]) > max) {
            max = Number(arr[len]);
        }
    }
    return max;
}
const MAGIC_FACTOR = 1.2 // see vls.archive.image.getOversizes()
;
export const getBestWebcacheFit = (targetWidth, sizes, orgWidth = 1000)=>{
    let available = sizes.filter((e)=>e <= 2000 || e * MAGIC_FACTOR < orgWidth);
    let filtered = available.filter((e)=>e > targetWidth);
    let reverse_sorted = filtered.sort((a, b)=>a - b);
    if (reverse_sorted.length > 0) {
        return reverse_sorted[0];
    } else {
        return available.at(-1);
    }
};
